import React, { FC, useMemo } from 'react';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ButtonStroke } from '@blink/components';
import { yupResolver } from '@hookform/resolvers/yup';
import Layout from 'antd/es/layout/layout';

import { BorrowAssets, BorrowRate, BorrowAmount, RiskFactor, BorrowAmountFields } from './fields';
import { useBorrowStyle } from './BorrowModal.style';

import { useModal } from '@/features/Modals';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { getErrorType } from '@/shared/utils/valiation';
import borrowModalSchema, { BorrowModalData } from '@/shared/validations/borrowModal.schema';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';
import { useGetRiskFactor } from '@/shared/hooks/useGetRiskFactor';
import { useGetAssetsToCollateral } from '@/shared/hooks/useGetAssetsToCollateral';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { useGetPrices } from '@/shared/hooks/useGetPrices';

export type SelectedBorrowAsset = {
    symbol: string;
    amount: string;
    amountInAssetToBorrow: string;
    address: string;
    name: string;
    decimals: number;
    maxBalance: string;
};

export const BorrowModal: FC = () => {
    const classes = useBorrowStyle();

    const { modalProps } = useModal();

    const dispatch = useDispatch();

    const { agreementAddress, tokenSymbol } = modalProps || {};

    const { data: assetsToCollateral, agreement } = useGetAssetsToCollateral({
        agreementAddress,
        role: ROLE.BORROWER,
    });

    const methods = useForm<BorrowModalData>({
        resolver: yupResolver(borrowModalSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const {
        riskFactor,
        stressTestedValue,
        collateralMarketValue,
        marketValue,
        collateralStressTested,
    } = useGetRiskFactor({
        agreement,
        assetSymbol: tokenSymbol,
        agreementAddress,
        fieldWatch: methods.watch,
    });

    useGetPrices({
        agreement,
        assetSymbol: tokenSymbol,
        agreementAddress,
        assetsToCollateral,
    });

    const onSubmit = (data: any) => {
        dispatch(
            showModalAction({
                modalName: ModalName.CONFIRMATION_TRANSACTION_MODAL,
                modalTitle: 'Review Transaction',
                modalProps: {
                    ...data,
                    agreement,
                    agreementAddress,
                    assetAddress: agreement.leverage.address,
                    tokenSymbol,
                    riskFactor,
                    stressTestedValue,
                    collateralStressTested,
                    collateralMarketValue,
                    marketValue,
                    border: false,
                },
            }),
        );
        return;
    };

    return (
        <div className={classes.wrapper}>
            <Layout>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className={classes.content}>
                            <div className={cn(classes.column, classes.left)}>
                                <BorrowAssets
                                    agreement={agreement}
                                    collateralAssets={assetsToCollateral}
                                />
                                <BorrowAmount
                                    agreement={agreement}
                                    collateralAssets={assetsToCollateral}
                                />
                                <BorrowRate
                                    collateralStressTested={collateralStressTested}
                                    agreement={agreement}
                                    collateralMarketValue={collateralMarketValue}
                                    agreementAddress={agreementAddress}
                                />
                                <SubmitButton health={riskFactor} agreement={agreement} />
                            </div>
                            <div className={classes.right}>
                                <RiskFactor
                                    health={riskFactor}
                                    agreement={agreement}
                                    collateralMarketValue={collateralMarketValue}
                                />
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </Layout>
        </div>
    );
};

function SubmitButton({ health, agreement }: { health: string | null; agreement: Agreement }) {
    const classes = useBorrowStyle();

    const { watch, formState } = useFormContext<BorrowModalData>();

    const { modalProps } = useModal();

    const { isHigh, loading, isLiquidation, isCloseToLiquidation } = useGetRiskLevel({
        health,
        marginCallThreshold: agreement.marginCallThreshold,
        liquidationThreshold: agreement.liquidationThreshold,
    });

    const { tokenSymbol } = modalProps || {};

    const borrowAmount = watch(BorrowAmountFields.ASSET_AMOUNT);

    const selectedAssets = watch('selectedAssets');

    const isDisabled = isLiquidation || loading || health === null || !formState.isValid;

    const getErrorMessage = (errors: any) => {
        if (!errors || !Object.keys(errors).length) return null;

        const errorType = getErrorType(errors);

        const zeroAmountAsset = selectedAssets?.find((asset: any) => Number(asset.amount) === 0);

        const tokenSymbolZero = zeroAmountAsset?.symbol || tokenSymbol;

        switch (errorType) {
            case 'is-valid-amount':
                return 'Collateral amount is invalid';
            case 'is-positive':
                return `Enter amount (${tokenSymbolZero})`;
            case 'is-valid-leverage-amount':
                return `Insufficient funds (${tokenSymbol})`;
            default:
                return `Enter amount (${tokenSymbol})`;
        }
    };

    const text = useMemo(() => {
        const errorMessage = getErrorMessage(formState.errors);

        if (errorMessage) return errorMessage;

        if (isLiquidation) {
            return `Risk Factor < ${agreement.liquidationThreshold} after operation`;
        }

        if (isCloseToLiquidation) {
            return 'Confirm transaction';
        }

        if (borrowAmount) {
            return 'Confirm transaction';
        }

        return `Enter amount (${tokenSymbol})`;
    }, [
        formState,
        isHigh,
        agreement.liquidationThreshold,
        borrowAmount,
        tokenSymbol,
        selectedAssets,
    ]);

    return (
        <div className={classes.btnWrapper}>
            <ButtonStroke
                className={cn(classes.submitButton, {
                    [classes.button]: isDisabled,
                })}
                disabled={isDisabled}
                isPrimary
                htmlType='submit'
            >
                {text}
            </ButtonStroke>
        </div>
    );
}

export default BorrowModal;
